import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { changeAudioSpeed, sameFormat, formatDuration, probeDuration } from "./operations";
import { fileSampleRate } from "./containers";
import { AudioProcessingProgress, showAudioError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Speeds up or slows down an audio file without changing its pitch — unlike simply playing a file faster (which raises pitch, the "chipmunk" effect), this keeps voices and music sounding natural at a different speed. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file, choose a speed (0.5x to 4x) and click Change Speed. You can listen to the result right on the page, with its exact length, before downloading; the trial use is only taken when you download. Pitch stays the same unless you also move the Pitch slider (up to an octave either way), which is separate from speed.`,
  tip: `This works well up to about 2x or down to about 0.5x — pushed further than that, audio can start to sound artifacted or robotic since the underlying technique has to stretch or compress the waveform more aggressively while trying to preserve pitch.`,
};

const fmt = formatDuration;
const pitchLabel = (st: number) => (st === 0 ? "unchanged" : `${st > 0 ? "+" : ""}${st} semitone${Math.abs(st) === 1 ? "" : "s"}`);
const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4];

export default function AudioSpeedChanger() {
  const [file, setFile] = useState<File | null>(null);
  const [speed, setSpeed] = useState([1]);
  const [duration, setDuration] = useState(0);
  const [semitones, setSemitones] = useState([0]);
  const [resultSeconds, setResultSeconds] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setDuration(0); setResultSeconds(null); setSemitones([0]);
    const url = URL.createObjectURL(f);
    const probe = new Audio();
    probe.preload = "metadata";
    probe.onloadedmetadata = () => { if (Number.isFinite(probe.duration)) setDuration(probe.duration); URL.revokeObjectURL(url); };
    probe.onerror = () => URL.revokeObjectURL(url);
    probe.src = url;
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const changeSpeed = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      const fmtOut = sameFormat(file.name);
      setStatusMessage(`Adjusting speed to ${speed[0]}x...`);
      // A pitch shift needs the file's own sample rate (the output keeps it); Opus and other rate-less formats run at 48 kHz.
      const pitch = semitones[0] ? { semitones: semitones[0], sampleRate: (await fileSampleRate(file)) ?? 48000 } : undefined;
      const data = await changeAudioSpeed(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, speed[0], setProgress, controller.signal, fmtOut.codec, pitch);

      const blob = ffmpegOutputToBlob(data, fmtOut.mime);
      setResult(blob);
      probeDuration(blob).then(setResultSeconds);
      toast.success("Speed adjusted!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file" description="Change playback speed without altering pitch" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <div className="space-y-2 max-w-sm">
            <Label>Speed: {Math.round(speed[0] * 100) / 100}x{duration ? ` (${fmt(duration)} → ${fmt(duration / speed[0])})` : ""}</Label>
            <Slider aria-label="Playback speed" value={speed} onValueChange={(v) => { setSpeed(v); setResult(null); }} min={0.5} max={4} step={0.05} />
            <div className="flex flex-wrap gap-1.5">
              {SPEED_PRESETS.map(s => (
                <Button key={s} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setSpeed([s]); setResult(null); }}>{s}x</Button>
              ))}
            </div>
          </div>

          <div className="space-y-2 max-w-sm">
            <Label>Pitch: {pitchLabel(semitones[0])}</Label>
            <Slider value={semitones} onValueChange={(v) => { setSemitones(v); setResult(null); }} min={-12} max={12} step={1} aria-label="Pitch in semitones" />
            <p className="text-xs text-muted-foreground">Leave this in the middle to keep the voice or notes as they are; it is separate from speed.</p>
          </div>

          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && (
            <div className="space-y-1" data-testid="speed-result">
              <p className="text-sm font-medium">Listen to the result{resultSeconds ? ` (${fmt(resultSeconds)} long)` : ""}</p>
              <p className="text-xs text-muted-foreground">Playing it here costs nothing; a trial use is only taken when you download.</p>
              <ResultPreview blob={result} />
            </div>
          )}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`speed_${speed[0]}x${semitones[0] ? `_pitch${semitones[0] > 0 ? "+" : ""}${semitones[0]}` : ""}_${file.name.replace(/\.[^.]+$/, `.${sameFormat(file.name).value}`)}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={changeSpeed} disabled={processing || sizeCheck?.severity !== "ok" || (speed[0] === 1 && semitones[0] === 0)}>{processing ? "Processing..." : "Change Speed"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
